import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";

import { Tooltip } from "@material-ui/core";
import Accordion from "@material-ui/core/Accordion";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import Grid from "@material-ui/core/Grid";

// SVG
import ExpandMoreIcon from "../../assets/svg/ExpandAccordionIcon.svg";
import EditIcon from "../../assets/svg/Pencil.svg";

// Files
import CustomAlert from "../../common/CustomAlert/CustomAlert";
import LinkButton from "../../common/LinkButton/LinkButton";
import Loader from "../../common/Loader/Loader";
import FulfillmentSection from "../../common/FulfillmentSection";

import { ALERT_TYPES, APP_ROUTES, SERVICE_TYPES, STATUS_OK, ZONE_CHANGE_STATUS } from "../../../config/constants";
import { selectServiceTypes, serviceSelectPropositionRecord } from "../../../config/redux/configurationsSlice";
import { activateDeactivateCollectionPoint, activateOrDeactivateAServiceOfPOS, activateOrDeactivateAServiceZoneOfPOS } from "../redux/completeSetupSlice";
import { PosStatusToggle } from "../../POSList/posUtils/PosAccessUtils";
import { isArrayValid, isEmptyOrNull } from "../../../utils/DataUtils";

// Styles
import slotsInformationStyles from "./SlotsInformationStyles";

// Pop Up
import ActivateDeactivateZoneErrorPopUp from "./common/ActivateDeactivateZoneErrorPopUp";
import { is_all_service_toggle_visible, is_configure_service_type_link_visible, is_edit_service_type_visible, is_user_can_create_approval_request } from "../CompleteSetupUtils";
import DetailedSlotInformationOnCompleteSetup from "./common/DetailedSlotInformationOnCompleteSetup";

const SlotsInformation = ({ posNo, posDetails, serviceTypeName, serviceTypeDisplayName, serviceTypeId, slotsInformation = {}, userInfo = {} }) => {
  const classes = slotsInformationStyles();
  const dispatch = useDispatch();

  const serviceTypes = useSelector(selectServiceTypes);
  const servicePropositionList = useSelector(serviceSelectPropositionRecord);

  const { loading, error, active, slots, toggling } = slotsInformation;

  const [selectedTab, setSelectedTab] = useState(() => {
    if (!slots || slots.length === 0) {
      return 0;
    }

    const tabIdFromZoneConfig = slots[0]?.zoneConfig?.id?.toString();
    const tabIdFromZone = slots[0]?.zone?.zoneConfig?.id?.toString();
    return tabIdFromZoneConfig || tabIdFromZone || 0;
  });

  const [expanded, setExpanded] = useState(false);
  const [activateDeactivateZoneError, setActivateDeactivateZoneError] = useState({
    showActivateDeactivateZoneErrorPopUp: false,
    activateDeactivateZoneErrorDetails: {},
  });

  const getCollectionPointDetailsByCollectionName = (cpName, allCollectionPoints) => {
    let allCollectionPointsObj = { ...allCollectionPoints };

    let cpDetails = allCollectionPointsObj[cpName]?.collectionPointDetails ?? {};
    return cpDetails || {};
  };

  const handleServiceActiveInactiveSwitch = async (e, selectedService) => {
    const { checked } = e.target;
    const {
      payload: { statusCode, message: error },
    } = await dispatch(activateOrDeactivateAServiceOfPOS({ posNo, serviceType: serviceTypeId, serviceTypeName: serviceTypeName.toUpperCase(), active: checked, })); // NOSONAR
    if (statusCode === STATUS_OK) {
      let message = checked ? `Service activated Successfully` : `Service de-activated Successfully`;
      CustomAlert(ALERT_TYPES.SUCCESS, message);
    }

    if (error) {
      CustomAlert(ALERT_TYPES.ERROR, error);
    }
  };

  const handleCollectionPointToggleSwitch = async (e, cpName, allCollectionPoints) => {
    const { checked } = e.target;
    const { collectionPointId: cpID, zoneId } = getCollectionPointDetailsByCollectionName(cpName, allCollectionPoints);

    const {
      payload: { status, message: error },
    } = await dispatch(activateDeactivateCollectionPoint({ serviceType: serviceTypeId, serviceTypeName: serviceTypeName.toUpperCase(), zoneId, collectionPointId: cpID, active: checked, })); // NOSONAR
    if (status === STATUS_OK) {
      let message = checked ? `Collection point - ${cpName} activated Successfully` : `Collection point - ${cpName} de-activated Successfully`;
      CustomAlert(ALERT_TYPES.SUCCESS, message);
    }

    if (error) {
      CustomAlert(ALERT_TYPES.SUCCESS, error);
    }
  };

  const handleActivateDeactivateZoneConfigClick = async (event, tabIndex, id) => {
    const { checked } = event.target;
    event.stopPropagation();
    const requestObject = {
      posNo,
      serviceType: serviceTypeId,
      serviceTypeName: serviceTypeName.toUpperCase(),
      activstatus: checked === true ? ZONE_CHANGE_STATUS.ACTIVATED : ZONE_CHANGE_STATUS.DEACTIVATED,
      tabIndex: tabIndex,
      zoneId: id,
      checked: checked,
    };
    const { payload } = await dispatch(activateOrDeactivateAServiceZoneOfPOS(requestObject)); // NOSONAR
    if (payload?.status === "OK") {
      if (Boolean(payload?.data?.success) === true) {
        let message = checked ? `Zone activated Successfully` : `Zone de-activated Successfully`;
        CustomAlert(ALERT_TYPES.SUCCESS, message);
      } else {
        setActivateDeactivateZoneError({
          showActivateDeactivateZoneErrorPopUp: true,
          activateDeactivateZoneErrorDetails: {
            message: payload.message,
            data: payload.data.data || [],
          },
        });
      }
    } else {
      CustomAlert(ALERT_TYPES.ERROR, payload.message);
    }
  };

  const is_all_pos_active_disable_status = (slotsRecord) => {
    if (!isArrayValid(slotsRecord) || isEmptyOrNull(slotsRecord)) {
      return false;
    }

    return slotsRecord.every((slot) => {
      return slot?.zoneConfig?.status === "DEACTIVATED" || slot?.zone?.zoneConfig?.status === "DEACTIVATED";
    });
  };

  const handle_close_activate_deactivate_zone_pop_up = () => {
    setActivateDeactivateZoneError({
      showActivateDeactivateZoneErrorPopUp: false,
      activateDeactivateZoneErrorDetails: {},
    });
  };

  const renderDetailedSlotInformationOnCompleteSetup = () => {
    if (!isEmptyOrNull(slots) && !isEmptyOrNull(selectedTab)) {
      return (
        <AccordionDetails className={classes.serviceAccDetails} onClick={(e) => e.stopPropagation()}>
          {slots.map((tab, tabIndex) => (
            <DetailedSlotInformationOnCompleteSetup
              key={tab?.zoneConfig?.id || tab?.zone?.zoneConfig?.id || tabIndex}
              classes={classes}
              userInfo={userInfo}
              toggling={toggling}
              tab={tab}
              tabIndex={tabIndex}
              serviceTypeName={serviceTypeName}
              handleActivateDeactivateZoneConfigClick={handleActivateDeactivateZoneConfigClick}
              handleCollectionPointToggleSwitch={handleCollectionPointToggleSwitch}
              servicePropositionList={servicePropositionList}
            />
          ))}
        </AccordionDetails>
      );
    }
    return null;
  }

  const renderSlotDetails = () => {
    if (slots !== undefined && isArrayValid(slots)) {
      return (
        <Accordion
          className={classes.activeAccordion}
          expanded={expanded}
          onClick={() => setExpanded(!expanded)}
        >
          <AccordionSummary
            expandIcon={
              <img src={ExpandMoreIcon} alt="Expand Accordion Icon" />
            }
            className={`${classes.accordionSummary} ${classes.serviceAccSummary}`}
          >
            <Grid container alignItems="center">
              <Grid item xs={7} className={classes.activeServiceType}>
                <div
                  className={classes.textCapitalize}
                >{`${serviceTypes.find((s) => s.name === serviceTypeName).displayName}`}</div>
              </Grid>
              <Grid item xs={5}>
                <Grid container justifyContent="flex-end">
                  <Grid item xs={3}>
                    {""}
                  </Grid>
                  {toggling ? (
                    <Grid item xs={5} className={classes.posRelative}>
                      <Loader />
                    </Grid>
                  ) : (
                    is_all_service_toggle_visible(userInfo) && (
                      <PosStatusToggle
                        userInfo={userInfo}
                        serviceTypeName={serviceTypeName}
                        classes={classes}
                        checked={Boolean(active)}
                        handleServiceActiveInactiveSwitch={(e) =>
                          handleServiceActiveInactiveSwitch(e, serviceTypeName)
                        }
                        disabled={is_all_pos_active_disable_status(slots)}
                      />
                    )
                  )}
                  <Grid item xs={2} className={classes.editServiceIcon}>
                    {is_edit_service_type_visible(userInfo) &&
                      (is_user_can_create_approval_request(userInfo) ? (
                        (serviceTypeName.toUpperCase() ===
                          SERVICE_TYPES.STANDARD ||
                          serviceTypeName.toUpperCase() ===
                            SERVICE_TYPES.EXPRESS) && (
                          <Link
                            className={classes.dFlex}
                            to={`${APP_ROUTES.CREATE_POS}${APP_ROUTES.SERVICE_AND_SLOTS}/${serviceTypeName.toLowerCase()}/${posNo}`}
                          >
                            <Tooltip
                              title={`${is_user_can_create_approval_request(userInfo) ? "Create a request for a new POS Zone configuration" : "Edit POS"}`}
                            >
                              <img src={EditIcon} alt="Edit POS" />
                            </Tooltip>
                          </Link>
                        )
                      ) : (
                        <Link
                          className={classes.dFlex}
                          to={`${APP_ROUTES.CREATE_POS}${APP_ROUTES.SERVICE_AND_SLOTS}/${serviceTypeName.toLowerCase()}/${posNo}`}
                        >
                          <Tooltip title={`Edit POS`}>
                            <img src={EditIcon} alt="Edit POS" />
                          </Tooltip>
                        </Link>
                      ))}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </AccordionSummary>
          <FulfillmentSection
            isReadOnly={true}
            posDetails={posDetails}
            serviceTypeId={serviceTypeId}
            classes={{
              ...classes,
              gridContainer: classes.fullfillmentSectionContainer,
              sectionTitle: classes.fullfillmentSectionContainerTitle,
            }}
          />
          {renderDetailedSlotInformationOnCompleteSetup()}
        </Accordion>
      );
    }
  }

  const renderServiceTypeLink = () => {
    if (is_configure_service_type_link_visible(userInfo)) {
      return (
        <Accordion className={classes.disableAccordion} expanded={expanded} onClick={undefined}>
          <AccordionSummary expandIcon={null} className={`${classes.accordionSummary} ${classes.serviceAccSummary}`}>
            <Grid container alignItems="center">
              <Grid item xs={7} className={classes.disabledServiceType}>
                <Link to={`${APP_ROUTES.CREATE_POS}${APP_ROUTES.SERVICE_AND_SLOTS}/${serviceTypeName.toLowerCase()}/${posNo}`} className={classes.linkstyle}>
                  <LinkButton className={classes.configureServiceButton} buttonLabel={`Configure ${serviceTypeDisplayName || serviceTypeName.toLowerCase()}`} />
                </Link>
              </Grid>
            </Grid>
          </AccordionSummary>
        </Accordion>
      );
    }
    return null;
  }

  const renderSlotInformation = () => {
    if (slots && isArrayValid(slots)) {
      return renderSlotDetails();
    } else {
      return renderServiceTypeLink();
    }
  }

  useEffect(() => {
    if (!error && !loading && !isEmptyOrNull(slots)) {
      const tabId = slots[0]?.zoneConfig?.id?.toString() || slots[0]?.zone?.zoneConfig?.id?.toString() || null;
      setSelectedTab(tabId);
    }
  }, [error, loading, slots]);

  return (
    <>
      <div className={classes.slotsInfoContainer}>
        {loading ? (<Loader />) : renderSlotInformation()}
      </div>

      {Boolean(activateDeactivateZoneError?.showActivateDeactivateZoneErrorPopUp) === true && (
        <ActivateDeactivateZoneErrorPopUp
          open={activateDeactivateZoneError.showActivateDeactivateZoneErrorPopUp}
          handleClose={handle_close_activate_deactivate_zone_pop_up}
          detailsObj={activateDeactivateZoneError.activateDeactivateZoneErrorDetails}
        />
      )}
    </>
  );
};

SlotsInformation.propTypes = {
  posNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  serviceTypeName: PropTypes.string.isRequired,
  serviceTypeDisplayName: PropTypes.string,
  serviceTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  slotsInformation: PropTypes.object,
  userInfo: PropTypes.object,
  posDetails: PropTypes.object,
};

export default SlotsInformation;
